// components/booking/BookingSuccessStep.tsx
import React from 'react';
import { BookingData } from './BookingWizard.tsx';
import { ModernButton } from '../ui/ModernButton.tsx';
import { ModernCard } from '../ui/ModernCard.tsx';
import { CheckIcon } from '../icons/index.tsx';

interface BookingSuccessStepProps {
  bookingData: BookingData;
  createdBooking: any;
  onStartOver: () => void;
}

export const BookingSuccessStep: React.FC<BookingSuccessStepProps> = ({
  bookingData,
  createdBooking,
  onStartOver
}) => {
  const formatSelectedDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-JO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'م' : 'ص';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getCourseName = () => {
    if (createdBooking?.course) {
      return createdBooking.course;
    }
    return bookingData.courseNotListed 
      ? bookingData.manualCourseName 
      : 'المادة المحددة';
  };

  const handleOpenCalendar = () => {
    if (createdBooking?.calendar_event_link) {
      window.open(createdBooking.calendar_event_link, '_blank', 'noopener,noreferrer');
    }
  };

  const copyBookingId = async () => {
    if (createdBooking?.id) {
      try {
        await navigator.clipboard.writeText(createdBooking.id.toString());
        // You might want to show a toast notification here
        alert('تم نسخ رقم الحجز');
      } catch (err) {
      }
    }
  };

  return (
    <div className="p-6 space-y-6 text-center">
      {/* Success Icon and Message */}
      <ModernCard className="p-8">
        <div className="mb-6">
          <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <CheckIcon className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">تم إنشاء الحجز بنجاح! 🎉</h2>
          <p className="text-gray-600 text-lg">تم حجز جلستك التعليمية بنجاح وإرسال الطلب للمدرسين</p>
        </div>

        {/* Booking ID */}
        {createdBooking?.id && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <p className="text-blue-800 font-semibold mb-1">رقم الحجز</p>
                <div className="flex items-center justify-center">
                  <span className="text-2xl font-mono font-bold text-blue-900">
                    #{createdBooking.id}
                  </span>
                  <ModernButton
                    variant="outline"
                    size="sm"
                    onClick={copyBookingId}
                    className="mr-3"
                  >
                    نسخ
                  </ModernButton>
                </div>
                <p className="text-blue-700 text-sm mt-1">
                  احتفظ برقم الحجز للرجوع إليه مستقبلاً
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Booking Details Summary */}
        <div className="space-y-4 mb-6">
          <ModernCard className="p-4 bg-gray-50">
            <h3 className="font-semibold text-gray-900 mb-3">تفاصيل الجلسة</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex items-center justify-center md:justify-start">
                  <span className="text-gray-600">📅</span>
                  <span className="mr-2">
                    <strong>التاريخ:</strong> {formatSelectedDate(bookingData.selectedDate)}
                  </span>
                </div>
                <div className="flex items-center justify-center md:justify-start">
                  <span className="text-gray-600">🕐</span>
                  <span className="mr-2">
                    <strong>الوقت:</strong> {formatTime(bookingData.selectedTime)}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-center md:justify-start">
                  <span className="text-gray-600">📚</span>
                  <span className="mr-2">
                    <strong>المادة:</strong> {getCourseName()}
                  </span>
                </div>
                <div className="flex items-center justify-center md:justify-start">
                  <span className="text-gray-600">🏛️</span>
                  <span className="mr-2">
                    <strong>الجامعة:</strong> {bookingData.university}
                  </span>
                </div>
              </div>
            </div>
          </ModernCard>
        </div>
        {/* 
        {/* Status and Next Steps */}
        {/*
        <div className="space-y-4">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="font-semibold text-yellow-900 mb-2">📋 الخطوات التالية:</h4>
            <div className="text-yellow-800 text-sm space-y-2 text-right">
              <div className="flex items-start">
                <span className="text-yellow-600 ml-2">•</span>
                <span>ستتلقى إشعاراً عند قبول أحد المدرسين للجلسة</span>
              </div>
              <div className="flex items-start">
                <span className="text-yellow-600 ml-2">•</span>
                <span>تم إضافة الجلسة إلى التقويم تلقائياً</span>
              </div>
              <div className="flex items-start">
                <span className="text-yellow-600 ml-2">•</span>
                <span>يمكنك متابعة حالة الحجز من قسم "حجوزاتي"</span>
              </div>
              <div className="flex items-start">
                <span className="text-yellow-600 ml-2">•</span>
                <span>في حالة عدم توفر مدرس، سنتواصل معك لإعادة الجدولة</span>
              </div>
            </div>
          </div>

          {bookingData.uploadedFiles.length > 0 && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm">
                ✅ تم رفع {bookingData.uploadedFiles.length} ملف مع الحجز
              </p>
            </div>
          )}
        </div>
        */}
      </ModernCard>

      {/* Action Buttons */}
      <div className="space-y-4">
        {/* Calendar Link */}
   

        {/* Primary Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ModernButton
            variant="outline"
            onClick={onStartOver}
            className="w-full"
          >
            حجز جلسة أخرى
          </ModernButton>
          
        </div>
      </div>

      {/* Contact Information */}
      <ModernCard className="p-4 bg-gray-50">
        <h4 className="font-semibold text-gray-900 mb-2">🤝 هل تحتاج مساعدة؟</h4>
        <p className="text-gray-700 text-sm mb-3">
          إذا كان لديك أي استفسار حول الحجز، لا تتردد في التواصل معنا
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <ModernButton
            variant="outline"
            size="sm"
            onClick={() => {
              // Open WhatsApp or contact method
              window.open('https://wa.me/96556501847', '_blank');
            }}
          >
            <span className="ml-1">💬</span>
            واتساب
          </ModernButton>
        
        </div>
      </ModernCard>

      {/* Thank You Message */}
      <div className="text-center pt-4">
        <p className="text-gray-600">
          شكراً لاختيارك خدماتنا التعليمية! 🙏
        </p>
        <p className="text-gray-500 text-sm mt-1">
          نتطلع لمساعدتك في رحلتك التعليمية
        </p>
      </div>
    </div>
  );
};